<template>
    <div class="threads-container">
        <div
            v-for="thread in mutatedThreads"
            :key="thread.id"
            class="thread"
            :class="{ 'support-thread': thread.is_support_thread }"
        >
            <div class="position-relative">
                <UserAvatar
                    v-if="thread.is_support_thread"
                    avatar-icon="fi-support"
                ></UserAvatar>

                <UserAvatarStack
                    v-else-if="thread.otherUsers"
                    :users="thread.otherUsers"
                    :clickable="true"
                />
                <div v-if="!thread.isRead" class="is-unread-badge"></div>
            </div>

            <div class="thread-main">
                <div class="d-flex justify-content-between">
                    <div class="thread-main">
                        <h4 class="thread-subject">
                            {{ thread.subject ?? '(No Subject)' }}
                        </h4>

                        <div class="text-muted">
                            {{ thread.metaDetails }}
                        </div>
                    </div>

                    <IconButton
                        v-if="false"
                        preset="delete"
                    />
                </div>

                <div
                    class="thread-msg-preview"
                    :class="{ 'text-bold': !thread.isRead }"
                >
                    {{
                        thread.messages.length ? thread.messages[0].message : ''
                    }}
                </div>

                <button
                    class="btn btn-primary ms-auto"
                    v-on:click="messengerStore.openThread(thread.id)"
                >
                    View Message
                    <i class="fi fi-chat"></i>
                </button>

                <div class="kebab-menu">
                    <KebabMenu
                        :menu-items="getMenuItems(thread)"
                        @thread-users="
                            messengerStore.openThreadUsers(thread.id)
                        "
                        @leave-thread="leaveThread(thread.id)"
                        @unread-thread="unreadThread(thread.id)"
                    />
                </div>
            </div>
        </div>

        <div v-if="!mutatedThreads.length" class="text-center">
            Click "New Message" to start a thread
        </div>
    </div>
</template>

<script>
import moment from 'moment';
//import Field from '@/components/Field.vue';
import UserAvatarStack from '../UserAvatarStack.vue';
import UserAvatar from '../UserAvatar.vue';
import { useMessengerStore } from '@/stores/messenger';
import KebabMenu from '@/components/KebabMenu.vue';
import IconButton from '@/components/IconButton.vue';

export default {
    name: 'MessageThreads',
    components: {
        //Field,
        UserAvatarStack,
        UserAvatar,
        KebabMenu,
        IconButton,
    },
    props: {},

    data() {
        return {
            yearFilter: null,
            messengerStore: null,
            threads: [],
        };
    },

    computed: {
        mutatedThreads() {
            if (!this.threads) return [];

            return structuredClone(this.threads).map((thread) => {
                if ('messages' in thread && thread.messages.length) {
                    const msg = thread.messages[0];
                    const author = this.$employeesStore.getEmployeeById(
                        msg.author_id
                    );

                    if (author) thread.author = author;
                }

                let metaDetailTokens = [];

                if ('author' in thread) {
                    const nameTokens = [];
                    if (thread.author.first_name)
                        nameTokens.push(thread.author.first_name);
                    if (thread.author.last_name)
                        nameTokens.push(thread.author.last_name);

                    if (nameTokens.length)
                        metaDetailTokens.push(nameTokens.join(' '));
                }

                // Second and third details: formatted date
                const createdAtLocal = moment.utc(thread.created_at).local();
                metaDetailTokens.push(createdAtLocal.format('MMM DD, YYYY'));
                metaDetailTokens.push(createdAtLocal.format('h:mma'));

                thread.metaDetails = metaDetailTokens.join(' • ');

                // Get the first user in the thread who isn't me
                const otherUserRelations = thread.users.filter(
                    (user) => user.user_id != this.$userStore.user.id
                );

                thread.otherUsers = otherUserRelations
                    .map((id) => {
                        return this.$employeesStore.getEmployeeById(id.user_id);
                    })
                    .filter((user) => user);

                if (!thread.otherUsers.length) {
                    thread.otherUsers = [
                        this.$employeesStore.getEmployeeById(
                            this.$userStore.user.id
                        ),
                    ];
                }

                thread.isRead = false;

                const myUserRelation = thread.users.find(
                    (user) => user.user.id == this.$userStore.user.id
                );

                if (myUserRelation) {
                    thread.isRead = !!myUserRelation.last_read;
                }

                return thread;
            });
        },
    },

    methods: {
        getMenuItems(thread) {
            const threadUser = thread?.users.find((threadUser) => {
                return threadUser.user.id == this.$userStore.user.id;
            });
            const exitedAt = threadUser?.exited_at;

            const isRead = !!threadUser?.last_read;

            let items = [{ label: 'View Members', click: 'thread-users' }];

            if (!exitedAt) {
                items.push({ label: 'Leave', click: 'leave-thread' });
            }

            if (isRead && !exitedAt) {
                items.push({ label: 'Mark as Unread', click: 'unread-thread' });
            }

            return items;
        },
        fetchThreads() {
            this.$getData('message-thread/read', this.getPayload).then(
                (response) => {
                    if (response.success) {
                        this.threads = response.data;
                    } else if (response.message)
                        this.$alertStore.pushError(response.message);
                }
            );
        },
        leaveThread(threadId) {
            this.$postData('message-thread/user/remove', {
                thread_id: threadId,
                user_id: this.$userStore.user.id,
            }).then((response) => {
                if (response?.success) {
                    this.fetchThreads();
                    this.$alertStore.pushSuccess(
                        'You have successfully left the thread'
                    );
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to leave the thread'
                    );
                }
            });
        },

        unreadThread(threadId) {
            this.$postData('message-thread/toggle-read', {
                thread_id: threadId,
                is_marked_read: false,
            }).then((response) => {
                if (response?.success) {
                    this.fetchThreads();
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to mark thread as unread'
                    );
                }
            });
        },
    },

    mounted() {
        this.fetchThreads();
        this.$employeesStore.sync();
    },

    created() {
        this.messengerStore = useMessengerStore();
    },
};
</script>

<style scoped>
.support-thread {
    background-color: var(--primary-25);
}

.threads-container {
    flex-grow: 1;
    padding-right: 0.5rem;
}

.thread {
    border-top: 1px solid var(--neutral-600, #e6e6e6);
    padding: var(--space-4) var(--space-3);
    display: inline-flex;
    gap: var(--space-4);
    width: 100%;
}

.thread:first-child {
    border-top: none;
}

.thread-main {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    position: relative;
}

.thread-subject {
    line-clamp: 1;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
    padding-right: 3rem;
}

.thread-msg-preview {
    line-clamp: 1;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
}

.text-bold {
    font-weight: 600;
}

.kebab-menu {
    position: absolute;
    top: 0;
    right: 0;
}

.is-unread-badge {
    display: block;
    background-color: var(--success);
    width: 0.625rem;
    height: 0.625rem;
    position: absolute;
    border-radius: 100%;
    margin-top: -2.7rem;
    margin-left: -0.55rem;
}
</style>
